import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
  InputGroup,
  Collapse
} from 'reactstrap';
import { FaList, FaEye, FaEdit, FaCreditCard, FaSearch, FaFilter, FaChevronDown, FaChevronUp, FaRedo, FaTimes, FaCheck } from 'react-icons/fa';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(20);
  const [totalOrders, setTotalOrders] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    state: '',
    domain: '',
    showFilters: false
  });

  // Modals
  const [statusModal, setStatusModal] = useState({ open: false, order: null });
  const [paymentModal, setPaymentModal] = useState({ open: false, order: null });
  const [detailModal, setDetailModal] = useState({ open: false, order: null });

  // Forms
  const [statusForm, setStatusForm] = useState({
    state: '',
    note: '',
    paid: false
  });
  const [paymentForm, setPaymentForm] = useState({
    paid: false,
    payment_method: '',
    payment_reference: '',
    note: ''
  });

  const orderStates = {
    'NEW': { label: 'New', className: 'bg-dark text-primary border border-primary' },
    'PAID_WAITING_EXDATE': { label: 'Paid - Waiting Expiry', className: 'bg-dark text-info border border-info' },
    'PAID_WAITING_CATCH': { label: 'Paid - Waiting Catch', className: 'bg-dark text-warning border border-warning' },
    'CATCHED_PROCESSING': { label: 'Caught - Processing', className: 'bg-dark text-secondary border border-secondary' },
    'CATCHED_COMPLETED': { label: 'Caught - Completed', className: 'bg-dark text-success border border-success' },
    'FAILED': { label: 'Failed', className: 'bg-dark text-danger border border-danger' },
    'CANCELLED': { label: 'Cancelled', className: 'bg-dark text-light border border-secondary' },
    'REFUNDED': { label: 'Refunded', className: 'bg-dark text-light border border-secondary' }
  };

  useEffect(() => {
    loadOrders();
    loadStats();
  }, [currentPage, filters.state, filters.domain]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage
      });

      if (filters.state) params.append('state', filters.state);
      if (filters.domain) params.append('domain', filters.domain);

      const response = await api.get(`/orders?${params.toString()}`);
      setOrders(response.data.orders);
      setTotalPages(response.data.pagination.pages);
      setTotalOrders(response.data.pagination.total);
    } catch (err) {
      if (err.response && err.response.status !== 404) {
        setError('Failed to load orders');
      }
      console.error('Load orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/orders/stats');
      setStats(response.data);
    } catch (err) {
      // Don't show error for stats if there are no orders
      console.error('Load stats error:', err);
    }
  };

  const loadOrderDetail = async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setDetailModal({ open: true, order: response.data });
    } catch (err) {
      setError('Failed to load order details');
      console.error('Load order detail error:', err);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await api.put(`/orders/${statusModal.order.id}/status`, statusForm);
      setSuccess('Order status updated successfully');
      setStatusModal({ open: false, order: null });
      setStatusForm({ state: '', note: '', paid: false });
      loadOrders();
      loadStats();
    } catch (err) {
      setError('Failed to update order status');
      console.error('Status update error:', err);
    }
  };

  const handlePaymentUpdate = async () => {
    try {
      await api.put(`/orders/${paymentModal.order.id}/payment`, paymentForm);
      setSuccess('Payment status updated successfully');
      setPaymentModal({ open: false, order: null });
      setPaymentForm({ paid: false, payment_method: '', payment_reference: '', note: '' });
      loadOrders();
      loadStats();
    } catch (err) {
      setError('Failed to update payment status');
      console.error('Payment update error:', err);
    }
  };

  const handleRestartOrder = async (order) => {
    if (!window.confirm(`Are you sure you want to restart order for ${order.domain}?\n\nThis will reset the order to:\n${order.paid_at ? '• PAID_WAITING_CATCH (order is paid)' : '• NEW (order is unpaid)'}`)) {
      return;
    }

    try {
      const response = await api.put(`/orders/${order.id}/restart`);
      setSuccess(response.data.message);
      loadOrders();
      loadStats();
    } catch (err) {
      setError('Failed to restart order');
      console.error('Restart order error:', err);
    }
  };

  const handleMarkAsFailed = async (order) => {
    if (!window.confirm(`Are you sure you want to mark order for ${order.domain} as FAILED?`)) {
      return;
    }

    try {
      await api.put(`/orders/${order.id}/status`, {
        state: 'FAILED',
        note: 'Marked as failed via quick action'
      });
      setSuccess(`Order #${order.id} marked as FAILED`);
      loadOrders();
      loadStats();
    } catch (err) {
      setError('Failed to update order status');
      console.error('Mark as failed error:', err);
    }
  };

  const handleApprove = async (order) => {
    if (!window.confirm(`Approve order for ${order.domain}?\n\nThis will:\n• Set status to PAID_WAITING_CATCH\n• Mark payment as PAID`)) {
      return;
    }

    try {
      // Update order status
      await api.put(`/orders/${order.id}/status`, {
        state: 'PAID_WAITING_CATCH',
        note: 'Approved via quick action'
      });

      // Update payment status
      await api.put(`/orders/${order.id}/payment`, {
        paid: true,
        payment_method: 'quick_approval',
        note: 'Quick approval'
      });

      setSuccess(`Order #${order.id} approved - ready for catching`);
      loadOrders();
      loadStats();
    } catch (err) {
      setError('Failed to approve order');
      console.error('Approve error:', err);
    }
  };

  const openStatusModal = (order) => {
    setStatusForm({
      state: order.state,
      note: '',
      paid: !!order.paid_at
    });
    setStatusModal({ open: true, order });
  };

  const openPaymentModal = (order) => {
    setPaymentForm({
      paid: !!order.paid_at,
      payment_method: order.payment_method || '',
      payment_reference: order.payment_reference || '',
      note: ''
    });
    setPaymentModal({ open: true, order });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : '-';
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="page-heading">
              <FaList className="page-heading-icon" />
              Orders Management
            </h2>
          </div>
        </Col>
      </Row>

      {error && <Alert color="danger" dismissible onDismiss={() => setError('')}>{error}</Alert>}
      {success && <Alert color="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}

      {/* Statistics Cards */}
      <Row className="mb-4">
        {Object.entries(stats.by_state || {}).map(([state, count]) => (
          <Col md="3" key={state} className="mb-3">
            <Card>
              <CardBody>
                <CardTitle tag="h6" className="text-muted mb-1">
                  {orderStates[state]?.label || state}
                </CardTitle>
                <h3 className="mb-0">{count}</h3>
              </CardBody>
            </Card>
          </Col>
        ))}
        <Col md="3" className="mb-3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">Total Value</CardTitle>
              <h3 className="mb-0">{formatCurrency(stats.total_amount || 0)}</h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <h5>
              <FaFilter className="me-2" />
              Filters
            </h5>
            <Button
              color="link"
              onClick={() => handleFilterChange('showFilters', !filters.showFilters)}
              className="p-0"
            >
              {filters.showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </Button>
          </div>
          <Collapse isOpen={filters.showFilters}>
            <hr />
            <Row>
              <Col md="4">
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={filters.state}
                    onChange={(e) => handleFilterChange('state', e.target.value)}
                    className="bg-secondary border-secondary text-white form-control"
                  >
                    <option value="">All States</option>
                    {Object.entries(orderStates).map(([value, config]) => (
                      <option key={value} value={value}>{config.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>Domain Search</Label>
                  <InputGroup>
                    <Input
                      type="text"
                      placeholder="Search domains..."
                      value={filters.domain}
                      onChange={(e) => handleFilterChange('domain', e.target.value)}
                      className="bg-secondary border-secondary text-white"
                    />
                    <Button color="primary">
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>&nbsp;</Label>
                  <div>
                    <Button
                      color="secondary"
                      onClick={() => {
                        setFilters({ state: '', domain: '', showFilters: true });
                        setCurrentPage(1);
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </Collapse>
        </CardBody>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Orders ({totalOrders})</h5>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-4">No orders found</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Domain</th>
                      <th>Drop Date</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Payment</th>
                      <th>User</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => (
                      <tr key={order.id}>
                        <td>#{order.id}</td>
                        <td>
                          <strong>{order.domain}</strong>
                          {order.tld && <div className="text-muted small">.{order.tld}</div>}
                        </td>
                        <td>{formatDate(order.drop_date)}</td>
                        <td>
                          <Badge className={orderStates[order.state]?.className || 'bg-dark text-secondary border border-secondary'}>
                            {orderStates[order.state]?.label || order.state}
                          </Badge>
                        </td>
                        <td>{formatCurrency(order.price)}</td>
                        <td>
                          {order.paid_at ? (
                            <Badge className="bg-dark text-success border border-success">Paid</Badge>
                          ) : (
                            <Badge className="bg-dark text-warning border border-warning">Pending</Badge>
                          )}
                        </td>
                        <td>{order.user?.username || '-'}</td>
                        <td>{formatDate(order.created_at)}</td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <Button
                              color="outline-info"
                              size="sm"
                              onClick={() => loadOrderDetail(order.id)}
                              title="View Details"
                            >
                              <FaEye size={14} />
                            </Button>
                            {(user?.role === 'admin' || user?.role === 'manager') && order.state === 'NEW' && (
                              <Button
                                color="outline-success"
                                size="sm"
                                onClick={() => handleApprove(order)}
                                title="Approve Order"
                              >
                                <FaCheck size={14} />
                              </Button>
                            )}
                            {(user?.role === 'admin' || user?.role === 'manager') && (
                              <Button
                                color="outline-warning"
                                size="sm"
                                onClick={() => openStatusModal(order)}
                                title="Update Status"
                              >
                                <FaEdit size={14} />
                              </Button>
                            )}
                            {(user?.role === 'admin' || user?.role === 'manager') && (
                              <Button
                                color="outline-success"
                                size="sm"
                                onClick={() => openPaymentModal(order)}
                                title="Manage Payment"
                              >
                                <FaCreditCard size={14} />
                              </Button>
                            )}
                            {(user?.role === 'admin' || user?.role === 'manager') && order.state !== 'FAILED' && (
                              <Button
                                color="outline-danger"
                                size="sm"
                                onClick={() => handleMarkAsFailed(order)}
                                title="Mark as Failed"
                              >
                                <FaTimes size={14} />
                              </Button>
                            )}
                            {(user?.role === 'admin' || user?.role === 'manager') && (
                              <Button
                                color="outline-primary"
                                size="sm"
                                onClick={() => handleRestartOrder(order)}
                                title="Restart Order"
                              >
                                <FaRedo size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <PaginationItem disabled={currentPage <= 1}>
                      <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                    </PaginationItem>
                    {[...Array(totalPages)].map((_, i) => (
                      <PaginationItem key={i} active={currentPage === i + 1}>
                        <PaginationLink onClick={() => setCurrentPage(i + 1)}>
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem disabled={currentPage >= totalPages}>
                      <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Status Update Modal */}
      <Modal isOpen={statusModal.open} toggle={() => setStatusModal({ open: false, order: null })}>
        <ModalHeader toggle={() => setStatusModal({ open: false, order: null })}>
          Update Order Status - {statusModal.order?.domain}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Status</Label>
              <Input
                type="select"
                value={statusForm.state}
                onChange={(e) => setStatusForm(prev => ({ ...prev, state: e.target.value }))}
                className="bg-secondary border-secondary text-white form-control"
              >
                {Object.entries(orderStates).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>
                <Input
                  type="checkbox"
                  checked={statusForm.paid}
                  onChange={(e) => setStatusForm(prev => ({ ...prev, paid: e.target.checked }))}
                />
                {' '}Mark as Paid
              </Label>
            </FormGroup>
            <FormGroup>
              <Label>Note</Label>
              <Input
                type="textarea"
                value={statusForm.note}
                onChange={(e) => setStatusForm(prev => ({ ...prev, note: e.target.value }))}
                className="bg-secondary border-secondary text-white"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleStatusUpdate}>
            Update Status
          </Button>
          <Button color="secondary" onClick={() => setStatusModal({ open: false, order: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Payment Update Modal */}
      <Modal isOpen={paymentModal.open} toggle={() => setPaymentModal({ open: false, order: null })}>
        <ModalHeader toggle={() => setPaymentModal({ open: false, order: null })}>
          Manage Payment - {paymentModal.order?.domain}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>
                <Input
                  type="checkbox"
                  checked={paymentForm.paid}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, paid: e.target.checked }))}
                />
                {' '}Payment Received
              </Label>
            </FormGroup>
            {paymentForm.paid && (
              <>
                <FormGroup>
                  <Label>Payment Method</Label>
                  <Input
                    type="select"
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="bg-secondary border-secondary text-white form-control"
                  >
                    <option value="">Select method...</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="paypal">PayPal</option>
                    <option value="crypto">Cryptocurrency</option>
                    <option value="other">Other</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label>Payment Reference</Label>
                  <Input
                    type="text"
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                    placeholder="Transaction ID, invoice number..."
                    className="bg-secondary border-secondary text-white"
                  />
                </FormGroup>
              </>
            )}
            <FormGroup>
              <Label>Note</Label>
              <Input
                type="textarea"
                value={paymentForm.note}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, note: e.target.value }))}
                className="bg-secondary border-secondary text-white"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handlePaymentUpdate}>
            Update Payment
          </Button>
          <Button color="secondary" onClick={() => setPaymentModal({ open: false, order: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Order Detail Modal */}
      <Modal isOpen={detailModal.open} toggle={() => setDetailModal({ open: false, order: null })} size="lg">
        <ModalHeader toggle={() => setDetailModal({ open: false, order: null })}>
          Order Details - {detailModal.order?.domain}
        </ModalHeader>
        <ModalBody>
          {detailModal.order && (
            <>
              <Row>
                <Col md="6">
                  <h6>Order Information</h6>
                  <p><strong>ID:</strong> #{detailModal.order.id}</p>
                  <p><strong>Domain:</strong> {detailModal.order.domain}</p>
                  <p><strong>Drop Date:</strong> {formatDate(detailModal.order.drop_date)}</p>
                  <p><strong>Status:</strong> <Badge className={orderStates[detailModal.order.state]?.className || 'bg-dark text-secondary border border-secondary'}>
                    {orderStates[detailModal.order.state]?.label || detailModal.order.state}
                  </Badge></p>
                  <p><strong>Price:</strong> {formatCurrency(detailModal.order.price)}</p>
                  <p><strong>Created:</strong> {formatDate(detailModal.order.created_at)}</p>
                </Col>
                <Col md="6">
                  <h6>Payment Information</h6>
                  <p><strong>Paid:</strong> {detailModal.order.paid_at ?
                    <Badge className="bg-dark text-success border border-success">Yes ({formatDate(detailModal.order.paid_at)})</Badge> :
                    <Badge className="bg-dark text-warning border border-warning">No</Badge>
                  }</p>
                  {detailModal.order.payment_method && (
                    <p><strong>Method:</strong> {detailModal.order.payment_method}</p>
                  )}
                  {detailModal.order.payment_reference && (
                    <p><strong>Reference:</strong> {detailModal.order.payment_reference}</p>
                  )}
                  {detailModal.order.notes && (
                    <div>
                      <strong>Notes:</strong>
                      <p className="text-muted">{detailModal.order.notes}</p>
                    </div>
                  )}
                </Col>
              </Row>

              {detailModal.order.history && detailModal.order.history.length > 0 && (
                <>
                  <hr />
                  <h6>Order History</h6>
                  <div className="table-responsive">
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Action</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Actor</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailModal.order.history.map(h => (
                          <tr key={h.id}>
                            <td>{formatDate(h.created_at)}</td>
                            <td>{h.action}</td>
                            <td>{h.from_state && <Badge className="bg-dark text-light border border-secondary">{orderStates[h.from_state]?.label || h.from_state}</Badge>}</td>
                            <td><Badge className={orderStates[h.to_state]?.className || 'bg-dark text-secondary border border-secondary'}>{orderStates[h.to_state]?.label || h.to_state}</Badge></td>
                            <td>{h.actor?.username || '-'}</td>
                            <td>{h.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDetailModal({ open: false, order: null })}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Orders;
